import { CharacterBlockConfig, CharacterBlockGroup } from '../types/codeGeneration';

/**
 * Character Block groups — reading them, checking them, and the presets an author
 * can choose from.
 *
 * Spec: docs/design/CHARACTER_BLOCK_GROUP_LABELS_SPEC.md.
 *
 * A Character Block that splits into groups emits ONE comb field per group, and
 * each of those fields needs its own accessible name. One field label cannot name
 * three controls, so the groups carry their own.
 *
 * This module is the frontend mirror of FormField.CharacterBlockConfig.resolveGroups()
 * on the server. The two must agree: the server decides how many fields get written,
 * and the editor has to show the author the same list it is going to name.
 */

/** Gap in points drawn at a group boundary by an applied preset. Matches the existing Quick Presets. */
const PRESET_GROUP_GAP = 10;

/**
 * The groups to show and to name, guaranteed non-empty.
 *
 * THE INVARIANT: a Character Block always has at least one group, so nothing
 * downstream branches on "is it grouped" — it iterates what this returns.
 *
 * Resolution order matches the server: an explicit `groups` list wins; otherwise the
 * legacy `groupPattern` ("3-2-4") is parsed into unlabelled groups, but only when it
 * agrees with totalBlocks (which drives layout); otherwise one group spanning
 * totalBlocks. Derived groups carry NO label, so an author who splits a legacy block
 * is asked to name the parts rather than shipping unnamed comb fields.
 */
export const resolveCharacterBlockGroups = (
  config?: Partial<CharacterBlockConfig> | null,
): CharacterBlockGroup[] => {
  if (config?.groups && config.groups.length > 0) {
    return config.groups;
  }
  const parsedTotal = Number(config?.totalBlocks);
  const total = isFinite(parsedTotal) && parsedTotal > 0 ? Math.floor(parsedTotal) : 1;

  const pattern = config?.groupPattern;
  if (pattern && pattern.trim() !== '') {
    const derived: CharacterBlockGroup[] = [];
    let sum = 0;
    pattern.split(/[^0-9]+/).forEach(part => {
      const n = parseInt(part, 10);
      if (!isNaN(n) && n > 0) {
        derived.push({ boxes: n });
        sum += n;
      }
    });
    // A pattern that disagrees with totalBlocks is stale - trust totalBlocks.
    if (derived.length > 0 && sum === total) {
      return derived;
    }
  }
  return [{ boxes: total }];
};

export interface CharacterBlockGroupPreset {
  id: string;
  /** What the author picks from the list. */
  name: string;
  groups: CharacterBlockGroup[];
}

/**
 * Presets an author chooses EXPLICITLY. Never pre-filled, never applied on the
 * author's behalf: an auto-filled label looks finished and ships exactly the failure
 * this feature exists to prevent, the same reason an accessible name is never derived
 * from the machine field name.
 *
 * The labels name what the digits MEAN ("area code") rather than where they sit
 * ("first three"), wherever the value has a meaning to name.
 */
export const CHARACTER_BLOCK_GROUP_PRESETS: CharacterBlockGroupPreset[] = [
  {
    id: 'phone',
    name: 'Phone number (3-3-4)',
    groups: [
      { boxes: 3, label: 'area code' },
      { boxes: 3, label: 'prefix' },
      { boxes: 4, label: 'line number' },
    ],
  },
  {
    id: 'ssn',
    name: 'Social Security number (3-2-4)',
    groups: [
      { boxes: 3, label: 'first three digits' },
      { boxes: 2, label: 'middle two digits' },
      { boxes: 4, label: 'last four digits' },
    ],
  },
  {
    id: 'date',
    name: 'Date (2-2-4)',
    groups: [
      { boxes: 2, label: 'month' },
      { boxes: 2, label: 'day' },
      { boxes: 4, label: 'year' },
    ],
  },
  {
    id: 'zip4',
    name: 'ZIP+4 (5-4)',
    groups: [
      { boxes: 5, label: 'ZIP code' },
      { boxes: 4, label: 'plus four' },
    ],
  },
];

/**
 * The division as a "3-2-4" string, for DISPLAY only.
 *
 * The one place the frontend turns groups into a pattern, mirroring the server's
 * CharacterBlockConfig.getGroupPattern(). Nothing stores the result: the editor used to
 * write groupPattern alongside groups and the two drifted apart, which is defect D3.
 */
export const characterBlockPattern = (groups: CharacterBlockGroup[]): string =>
  groups.map(group => Math.max(Number(group.boxes) || 1, 1)).join('-');

export const characterBlockBoxCount = (groups: CharacterBlockGroup[]): number =>
  groups.reduce((sum, group) => sum + (Number(group.boxes) || 0), 0);

export const characterBlockNamedCount = (groups: CharacterBlockGroup[]): number =>
  groups.filter(group => (group.label || '').trim() !== '').length;

/**
 * Where a preset's group boundaries fall, expressed in the layout fields the
 * renderer already understands (0-based "gap after this position"). Only the first
 * two boundaries can be drawn — that is all the layout model carries — so a preset
 * of four or more groups is named correctly but drawn with two visible gaps.
 */
export const gapLayoutForGroups = (groups: CharacterBlockGroup[]) => {
  const boundaries: number[] = [];
  let running = 0;
  groups.slice(0, -1).forEach(group => {
    running += Number(group.boxes) || 0;
    boundaries.push(running);
  });
  return {
    firstGap: boundaries.length > 0 ? PRESET_GROUP_GAP : 0,
    firstGapPosition: boundaries.length > 0 ? boundaries[0] - 1 : undefined,
    secondGap: boundaries.length > 1 ? PRESET_GROUP_GAP : 0,
    secondGapPosition: boundaries.length > 1 ? boundaries[1] - 1 : undefined,
  };
};

/**
 * The rules that BLOCK generation (spec section 5). Everything here produces an
 * unnamed or unwritable comb field, so none of it can be shipped.
 *
 * The softer rules - a group label repeating the field label, two groups sharing a
 * label - are warnings, and live in characterBlockGroupWarnings so they never stop
 * a form being generated.
 */
export const characterBlockGroupErrors = (
  config?: Partial<CharacterBlockConfig> | null,
): string[] => {
  const errors: string[] = [];
  const groups = resolveCharacterBlockGroups(config);

  if (groups.some(group => !(Number(group.boxes) > 0))) {
    errors.push('Every group needs at least one box');
  }

  const total = Number(config?.totalBlocks);
  const boxes = characterBlockBoxCount(groups);
  if (isFinite(total) && total > 0 && boxes !== total) {
    errors.push(`Groups add up to ${boxes} boxes but this block has ${total}`);
  }

  if (groups.length > 1) {
    const unnamed = groups.length - characterBlockNamedCount(groups);
    if (unnamed > 0) {
      errors.push(
        `${unnamed} of ${groups.length} groups still needs a name — each group becomes its own field`,
      );
    }
  }

  return errors;
};

/**
 * The rules that WARN (spec section 5). Both produce a form that generates and reads
 * out, just worse: a group named after the field repeats itself in every announcement,
 * and two groups sharing a name announce identically.
 */
export const characterBlockGroupWarnings = (
  groups: CharacterBlockGroup[],
  fieldLabel?: string,
): string[] => {
  const warnings: string[] = [];
  if (groups.length < 2) return warnings;

  const normalised = groups.map(group => (group.label || '').trim().toLowerCase());
  const field = (fieldLabel || '').trim().toLowerCase();

  if (field !== '' && normalised.some(label => label !== '' && label === field)) {
    warnings.push(
      'A group has the same name as the field, so it will be announced twice — name the part, not the whole',
    );
  }

  const seen: string[] = [];
  const repeated: string[] = [];
  normalised.forEach(label => {
    if (label === '') return;
    if (seen.indexOf(label) >= 0 && repeated.indexOf(label) < 0) repeated.push(label);
    seen.push(label);
  });
  if (repeated.length > 0) {
    warnings.push('Two groups share a name, so they will be announced identically');
  }

  return warnings;
};
